import {
  UIPaths,
  UPLOAD_ARCHIVE_ACCEPT_CONTENT_TYPES,
  UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES,
  UPLOAD_DOCUMENT_ACCEPT_CONTENT_TYPES,
  UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES,
  UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES
} from '@consts';
import type { ImprvApiService } from '@hooks/useImprvApi';
import { resolveContentType, resolveContentTypeWithMagic } from '@mime';
import { CreateMediaitemModel, MediaitemType, MediaitemUsageType } from '@models';
import {
  BackgroundActivityKind,
  BackgroundActivityTargetKind,
  BackgroundJobActivityTarget
} from '@models/backgroundActivities';
import * as Sentry from '@sentry/nextjs';
import AwsS3 from '@uppy/aws-s3';
import type { Uppy } from '@uppy/core';
import UppyDefault from '@uppy/core';

import { useTranscriptExpectationsStore } from '../stores/transcript_expectations_store';
import type { BackgroundActivityRegistry } from './backgroundActivityRegistry';

const contentTypeToMediaitemType: Record<string, MediaitemType> = {
  ...Object.fromEntries(UPLOAD_IMAGE_ACCEPT_CONTENT_TYPES.map((t) => [t, MediaitemType.IMAGE])),
  ...Object.fromEntries(UPLOAD_VIDEO_ACCEPT_CONTENT_TYPES.map((t) => [t, MediaitemType.VIDEO])),
  ...Object.fromEntries(UPLOAD_AUDIO_ACCEPT_CONTENT_TYPES.map((t) => [t, MediaitemType.AUDIO])),
  ...Object.fromEntries(
    UPLOAD_DOCUMENT_ACCEPT_CONTENT_TYPES.map((t) => [t, MediaitemType.DOCUMENT])
  ),
  ...Object.fromEntries(UPLOAD_ARCHIVE_ACCEPT_CONTENT_TYPES.map((t) => [t, MediaitemType.ARCHIVE]))
};

interface UploadServiceDeps {
  api: ImprvApiService;
  registry: BackgroundActivityRegistry;
}

interface UploadContext {
  uppy: Uppy;
  basePath?: string;
  contentType?: string;
}

export class MediaitemUploadService {
  private deps: UploadServiceDeps;
  private contextByActivityId: Map<string, UploadContext> = new Map();

  constructor(deps: UploadServiceDeps) {
    this.deps = deps;
  }

  startUpload(params: {
    target: BackgroundJobActivityTarget;
    file: File;
    mediaitemUsageType: MediaitemUsageType;
    returnUrl?: string;
  }): string {
    const resolvedMime = resolveContentType(params.file.name, params.file.type || '');
    const mediaitemType = contentTypeToMediaitemType[resolvedMime] ?? null;

    const uppy = new UppyDefault({
      restrictions: { maxNumberOfFiles: 1 },
      autoProceed: true
    });

    const activityId = this.deps.registry.register({
      kind: BackgroundActivityKind.UPLOAD,
      target: params.target,
      payload: {
        filename: params.file.name,
        mediaitemType,
        mediaitemUsageType: params.mediaitemUsageType
      },
      onCancel: () => this.teardown(activityId)
    });

    const context: UploadContext = { uppy };
    this.contextByActivityId.set(activityId, context);

    uppy.use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async (file: { name: string; type?: string; size?: number }) => {
        const contentType = resolveContentType(file.name, file.type || '');
        try {
          const sig = await this.deps.api.signMediaitemUpload(contentType, file.size ?? 0);
          context.basePath = sig.basePath;
          context.contentType = contentType;
          return {
            method: 'PUT',
            url: sig.uploadUrl,
            headers: { 'Content-Type': contentType }
          };
        } catch (err) {
          Sentry.captureException(err);
          throw err;
        }
      }
    });

    uppy.on('upload', () => {
      this.deps.registry.markRunning(activityId);
    });

    uppy.on('upload-progress', (_file: any, progress: any) => {
      if (!progress.bytesTotal) return;
      const pct = Math.round((100 * progress.bytesUploaded) / progress.bytesTotal);
      this.deps.registry.updateProgress(activityId, pct);
    });

    uppy.on('upload-success', async (file: any) => {
      if (!file) return;
      const { basePath } = context;
      if (!basePath) return;
      const contentType = context.contentType ?? resolveContentType(file.name, file.type || '');
      const payload: CreateMediaitemModel = {
        type: mediaitemType ?? contentTypeToMediaitemType[contentType] ?? MediaitemType.DOCUMENT,
        usageType: params.mediaitemUsageType,
        basePath,
        contentType,
        originalFilename: file.name
      };
      try {
        const created = await this.deps.api.createMediaitem(payload);
        if (
          params.target.kind === BackgroundActivityTargetKind.TASK_BLOCK_CONTENT &&
          payload.type === MediaitemType.VIDEO
        ) {
          useTranscriptExpectationsStore.getState().recordExpectation(created.id, {
            taskEntityId: params.target.taskEntityId,
            returnUrl: params.returnUrl ?? UIPaths.taskEditor(params.target.taskEntityId),
            filename: file.name,
            createdAt: Date.now()
          });
        }
        this.deps.registry.complete(activityId, BackgroundActivityKind.UPLOAD, {
          mediaitemId: created.id,
          originalFilename: file.name,
          contentType
        });
      } catch (err) {
        Sentry.captureException(err);
        this.deps.registry.fail(activityId, 'components.fileInput.errorUpload');
      } finally {
        this.teardown(activityId);
      }
    });

    uppy.on('upload-error', (_file: any, err: any) => {
      Sentry.captureException(err);
      this.deps.registry.fail(activityId, 'components.fileInput.errorUpload');
      this.teardown(activityId);
    });

    resolveContentTypeWithMagic(params.file.name, params.file.type || '', () =>
      params.file.slice(0, 16).arrayBuffer()
    )
      .then((mime) => {
        uppy.addFile({
          source: 'mediaitem-upload-service',
          name: params.file.name,
          type: mime,
          data: params.file
        });
      })
      .catch((err) => {
        Sentry.captureException(err);
        this.deps.registry.fail(activityId, 'components.fileInput.errorUpload');
        this.teardown(activityId);
      });

    return activityId;
  }

  private teardown(activityId: string): void {
    const context = this.contextByActivityId.get(activityId);
    if (context) {
      context.uppy.cancelAll();
      context.uppy.destroy();
      this.contextByActivityId.delete(activityId);
    }
  }
}
